import { Controller } from '@hotwired/stimulus'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export default class extends Controller {
  static targets = ['scrim', 'panel', 'closeButton', 'body']
  static values = {
    id: String,
    state: { type: String, default: 'closed' },
  }

  connect() {
    this.boundOpenEvent = this.handleOpenEvent.bind(this)
    this.boundCloseEvent = this.handleCloseEvent.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)

    document.addEventListener('slideover:open', this.boundOpenEvent)
    document.addEventListener('slideover:close', this.boundCloseEvent)

    if (this.stateValue === 'open') this.applyOpenStyles()
  }

  disconnect() {
    document.removeEventListener('slideover:open', this.boundOpenEvent)
    document.removeEventListener('slideover:close', this.boundCloseEvent)
    document.removeEventListener('keydown', this.boundKeydown)
    this.unlockBodyScroll()
  }

  // Stimulus action targets
  open() {
    this.show()
  }

  close() {
    this.hide()
  }

  // Global event listeners
  handleOpenEvent(event) {
    if (event.detail?.id !== this.idValue) return
    this.lastTrigger = event.detail.trigger || null
    this.show()
  }

  handleCloseEvent(event) {
    if (event.detail?.id && event.detail.id !== this.idValue) return
    this.hide()
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.hide()
      return
    }
    if (event.key === 'Tab') {
      this.cycleFocus(event)
    }
  }

  // Visibility
  show() {
    if (this.stateValue === 'open') return

    this.element.hidden = false
    // Force a reflow so the transition picks up the next-tick class change.
    void this.element.offsetWidth
    this.applyOpenStyles()
    this.lockBodyScroll()
    document.addEventListener('keydown', this.boundKeydown)
    this.focusFirst()
    this.stateValue = 'open'

    this.dispatch('opened', { detail: { id: this.idValue } })
  }

  hide() {
    if (this.stateValue === 'closed') return

    this.applyClosedStyles()
    document.removeEventListener('keydown', this.boundKeydown)
    this.unlockBodyScroll()
    this.stateValue = 'closed'

    const onTransitionEnd = (event) => {
      if (event.target !== this.panelTarget) return
      this.panelTarget.removeEventListener('transitionend', onTransitionEnd)
      this.element.hidden = true
      this.restoreFocus()
      this.dispatch('closed', { detail: { id: this.idValue } })
    }
    this.panelTarget.addEventListener('transitionend', onTransitionEnd)
  }

  // Style application
  applyOpenStyles() {
    this.scrimTarget.classList.remove('opacity-0')
    this.scrimTarget.classList.add('opacity-100')
    this.panelTarget.classList.remove('translate-x-full')
    this.panelTarget.classList.add('translate-x-0')
  }

  applyClosedStyles() {
    this.scrimTarget.classList.remove('opacity-100')
    this.scrimTarget.classList.add('opacity-0')
    this.panelTarget.classList.remove('translate-x-0')
    this.panelTarget.classList.add('translate-x-full')
  }

  // Body scroll lock
  lockBodyScroll() {
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  unlockBodyScroll() {
    if (this.previousBodyOverflow === undefined) return
    document.body.style.overflow = this.previousBodyOverflow
    this.previousBodyOverflow = undefined
  }

  // Focus
  focusFirst() {
    const focusables = this.focusableElements()
    const target = focusables[0] || this.panelTarget
    target.focus({ preventScroll: true })
  }

  restoreFocus() {
    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus({ preventScroll: true })
      this.lastTrigger = null
    }
  }

  focusableElements() {
    return Array.from(this.panelTarget.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.hasAttribute('hidden') && el.offsetParent !== null
    )
  }

  cycleFocus(event) {
    const focusables = this.focusableElements()
    if (focusables.length === 0) {
      event.preventDefault()
      this.panelTarget.focus()
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}
